import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  History,
  CheckCircle,
  Archive,
  RotateCcw,
  FileText,
  Users,
  Calendar,
  Clock,
  AlertTriangle,
  Download,
  Eye
} from 'lucide-react';

/**
 * VersionControl - Version history and rollback interface
 * 
 * Displays version history for a session with ability to view details,
 * rollback to previous versions, and manage version lifecycle.
 * 
 * Features:
 * - Chronological version history
 * - Version comparison
 * - Rollback functionality with impact warnings
 * - Version status management
 * - Usage statistics per version
 */
const VersionControl = ({ session, versions, onRollback }) => {
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (version) => {
    if (version.isCurrent) {
      return <Badge className="bg-green-100 text-green-800">Current</Badge>;
    }
    
    switch (version.status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'deprecated':
        return <Badge variant="secondary">Deprecated</Badge>;
      case 'archived':
        return <Badge variant="outline">Archived</Badge>;
      default:
        return null;
    }
  };

  const handleRollback = (versionId) => {
    setShowRollbackConfirm(versionId);
  };

  const confirmRollback = (versionId) => {
    onRollback(versionId);
    setShowRollbackConfirm(null);
  };

  const toggleVersionSelect = (versionId) => {
    setSelectedVersions(prev => 
      prev.includes(versionId) 
        ? prev.filter(id => id !== versionId)
        : [...prev, versionId]
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <History className="h-5 w-5" />
                <span>Version History</span>
              </CardTitle>
              <CardDescription>
                Manage versions for {session.name} and rollback if needed
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              {selectedVersions.length === 2 && (
                <Button variant="outline" size="sm" className="flex items-center space-x-2">
                  <Eye className="h-4 w-4" />
                  <span>Compare Selected</span>
                </Button>
              )}
              <Button variant="outline" size="sm" className="flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export History</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {versions.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No version history available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {versions.map((version, index) => (
                <div key={version.id} className="relative">
                  {/* Timeline line */}
                  {index < versions.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-full bg-gray-200"></div>
                  )}
                  
                  <div className={`
                    p-6 rounded-lg border transition-all duration-200
                    ${version.isCurrent 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}>
                    {/* Version Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-4">
                        {/* Timeline dot */}
                        <div className={`
                          relative z-10 flex-shrink-0 w-3 h-3 rounded-full mt-2
                          ${version.isCurrent ? 'bg-green-500' : 'bg-gray-300'}
                        `}>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {version.versionId}
                            </h3>
                            {getStatusBadge(version)}
                            {version.isCurrent && (
                              <Badge className="bg-blue-100 text-blue-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                In Use
                              </Badge>
                            )}
                            <input
                              type="checkbox"
                              checked={selectedVersions.includes(version.versionId)}
                              onChange={() => toggleVersionSelect(version.versionId)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                            <span className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(version.createdAt)}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <span>by {version.createdBy}</span>
                            </span>
                          </div>

                          {version.changeNotes && (
                            <p className="text-gray-700 text-sm mb-3">
                              {version.changeNotes}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        {!version.isCurrent && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRollback(version.versionId)}
                            className="flex items-center space-x-2"
                          >
                            <RotateCcw className="h-4 w-4" />
                            <span>Rollback</span>
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="flex items-center space-x-2"
                        >
                          <Eye className="h-4 w-4" />
                          <span>View</span>
                        </Button>
                      </div>
                    </div>

                    {/* Version Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-white rounded-lg border">
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 text-gray-500 text-xs mb-1">
                          <FileText className="h-3 w-3" />
                          <span>Files</span>
                        </div>
                        <div className="font-semibold">{version.filesCount}</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 text-gray-500 text-xs mb-1">
                          <Archive className="h-3 w-3" />
                          <span>Size</span>
                        </div>
                        <div className="font-semibold">{version.totalSize}</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 text-gray-500 text-xs mb-1">
                          <Users className="h-3 w-3" />
                          <span>SOs Using</span>
                        </div>
                        <div className="font-semibold">{version.sosUsingVersion}</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1 text-gray-500 text-xs mb-1">
                          <CheckCircle className="h-3 w-3" />
                          <span>Status</span>
                        </div>
                        <div className="font-semibold text-xs capitalize">{version.status}</div>
                      </div>
                    </div>

                    {/* Rollback Confirmation */}
                    {showRollbackConfirm === version.versionId && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-medium text-amber-800 mb-2">
                              Confirm Rollback to {version.versionId}
                            </h4>
                            <p className="text-sm text-amber-700 mb-3">
                              This will affect <strong>{session.activeSOs} active SOs</strong> using this session. 
                              New SOs created after rollback will use this version.
                            </p>
                            <div className="flex space-x-2">
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => confirmRollback(version.versionId)}
                                className="flex items-center space-x-2"
                              >
                                <RotateCcw className="h-4 w-4" />
                                <span>Confirm Rollback</span>
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setShowRollbackConfirm(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Version Management Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Version Management</CardTitle>
          <CardDescription>
            Advanced operations for version lifecycle management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <Archive className="h-4 w-4" />
              <span>Archive Old Versions</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>Bulk Version Operations</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span>Schedule Version Update</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export Version Report</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Version Statistics Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Version Statistics</CardTitle>
          <CardDescription>
            Overview of version usage and performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {versions.filter(v => v.status === 'active').length}
              </div>
              <div className="text-sm text-gray-500">Active Versions</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {versions.reduce((sum, v) => sum + v.sosUsingVersion, 0)}
              </div>
              <div className="text-sm text-gray-500">Total SO Usage</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {versions.reduce((sum, v) => sum + v.filesCount, 0)}
              </div>
              <div className="text-sm text-gray-500">Total Files</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VersionControl;